// Per-type notification opt-outs and quiet hours.
//
// Deliberately mirrors apps/mobile/src/hooks/use-notification-settings.ts —
// same endpoint, same query key, same shape — so the two settings screens
// cannot drift. Only the api-client import and the error toast differ.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  DEFAULT_NOTIFICATION_USER_SETTINGS,
  MUTABLE_NOTIFICATION_CATEGORIES,
  type NotificationTypeGroupDto,
  type NotificationUserSettings,
  type UpdateNotificationSettingsInput,
} from '@task-tracker/shared';
import { apiClient } from '@/lib/api-client';
import { CACHE_POLICY } from '@/lib/cache-policy';

export const notificationSettingsKey = ['notifications', 'settings'] as const;

const QUERY_KEY = notificationSettingsKey;

interface NotificationSettingsResponse {
  settings: NotificationUserSettings;
  categories: string[];
  typeGroups: NotificationTypeGroupDto[];
}

/**
 * The single owner of `['notifications','settings']`.
 *
 * There must be exactly one: `useRealtimeNotifications` reads it from the bell
 * (mounted on every authenticated page) while the Settings screen reads it too,
 * and a second hook on the same key with a narrower response shape leaves the
 * cache holding a payload without `typeGroups` — which the type toggles render
 * as "settings unavailable".
 *
 * `reference` tier, not `live`: web has refetchOnWindowFocus enabled, so without
 * the 5-minute staleTime every window focus would refetch this app-wide, for
 * data only the user themselves changes from a screen that invalidates the key
 * anyway.
 */
export function useNotificationSettings() {
  return useQuery({
    ...CACHE_POLICY.reference,
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const result = await apiClient<NotificationSettingsResponse>(
        '/api/v1/notifications/settings',
      );
      return {
        settings: result.data?.settings ?? { ...DEFAULT_NOTIFICATION_USER_SETTINGS },
        categories: result.data?.categories ?? MUTABLE_NOTIFICATION_CATEGORIES,
        // An older Worker predating typeGroups returns undefined; render nothing
        // rather than crashing the screen.
        typeGroups: result.data?.typeGroups ?? [],
      };
    },
  });
}

export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: UpdateNotificationSettingsInput) => {
      const result = await apiClient<{ settings: NotificationUserSettings }>(
        '/api/v1/notifications/settings',
        { method: 'PATCH', body: JSON.stringify(updates) },
      );
      return result.data?.settings ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => {
      // The toggles are optimistic; without this a failed save looks like it
      // worked until the next refetch silently reverts it.
      toast.error('Could not save your notification settings.');
    },
  });
}
